import React from 'react';
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Divider,
  Grid,
  Button,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthenticated } from '../hooks/useAuthenticated';
import { useQuery } from '@apollo/client';
import { GET_ME } from '../graphql/getMe';

const labelColor = '#1a237e';
const chipGray = '#bdbdbd';
const chipGreen = '#4CAF50';
const chipRed = '#FF6B6B';
const chipPurple = 'linear-gradient(135deg, #7b61ff 30%, #a084ee 90%)';

const Profile = () => {
  const { data: me, loading } = useQuery(GET_ME);
  const { isPlanActive } = useAuthenticated();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '111.11vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const user = me?.getMe;
  if (!user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '111.11vh' }}>
        <Typography>User not found</Typography>
      </Box>
    );
  }

  const userInitial = user.name ? user.name.charAt(0).toUpperCase() : 'U';
  const planExpiryDate = user.planExpiresAt
    ? new Date(user.planExpiresAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : 'No plan';

  return (
    <Box
      sx={{
        minHeight: '111.11vh',
        background: 'linear-gradient(to bottom, #e3f8ff, #78a2f0)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2, // Increased padding
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 600, // Increased maxWidth
          background: '#fff',
          borderRadius: 6, // Slightly more rounded
          boxShadow: '0 6px 32px 0 rgba(0,0,0,0.09)', // Slightly larger shadow
          padding: { xs: 4, md: 6 }, // Increased padding
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start', // Align left
        }}
      >
        {/* Avatar and User Info */}
        <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              width: 90, // Larger avatar
              height: 90,
              fontSize: '3rem',
              background: chipPurple,
              mb: 2,
            }}
          >
            {userInitial}
          </Avatar>
          <Typography variant="h5" sx={{ fontWeight: 500, color: labelColor, mb: 0.5, fontFamily: 'Poppins, sans-serif', textAlign: 'center' }}>
            {user.name || 'User'}
          </Typography>
          <Typography variant="body1" sx={{ color: '#757575', fontSize: 14, mb: 1, textAlign: 'center' }}>
            {user.email}
          </Typography>
        <Chip
          label={'User'}
          sx={{
            background: chipGray,
            color: '#fff',
            fontWeight: 600,
            fontSize: 14,
            borderRadius: '16px',
            height: 26,
            px: 2,
            boxShadow: 'none',
            ml: 0.5, 
            alignSelf: 'center', 
          }}
        />
        </Box>
        <Divider sx={{ width: '100%', my: 1 }} />
        <Box sx={{ width: '100%', mb: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                <Typography sx={{ color: '#757575', fontWeight: 600, fontSize: 14, mb: 0.25 }}>
                  Plan
                </Typography>
                <Typography sx={{ color: labelColor, fontWeight: 600, fontSize: 18 }}>
                  {user.planName || 'No Plan'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 0.5 }}>
                <Typography sx={{ color: '#757575', fontWeight: 600, fontSize: 14, mb: 0.25}}>
                  Status
                </Typography>
                <Chip
                  label={isPlanActive ? 'Active' : 'Inactive'}
                  sx={{
                    background: isPlanActive ? chipGreen : chipRed,
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 14,
                    borderRadius: '16px',
                    height: 28,
                    px: 3,
                    boxShadow: 'none',
                    alignSelf: 'flex-start', 
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <Typography sx={{ color: '#757575', fontWeight: 600, fontSize: 14 }}>
                  Expiry Date
                </Typography>
                <Typography sx={{ color: labelColor, fontWeight: 600, fontSize: 18 }}>
                  {planExpiryDate}
                </Typography>
              </Box>
            </Grid>
            {!isPlanActive && (
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  mt: 2,
                  p: 3,
                  background: 'linear-gradient(135deg,rgb(255, 252, 224) 0%,rgb(253, 232, 141) 100%)',
                  borderRadius: 2,
                }}>
                  <Typography sx={{ color: '#e65100', fontWeight: 600, fontSize: 16, mb: 1 }}>
                    Plan Inactive
                  </Typography>
                  <Typography sx={{ color: '#bf360c', fontSize: 14, mb: 2 }}>
                    {user.planExpiresAt ? 'Your plan has expired. Upgrade now to continue accessing all features.' : 'You do not have an active plan. Choose a plan to begin your board exam preparation.'}
                  </Typography>
                  <Button
                    variant="contained"
                    onClick={() => navigate('/pricing')}
                    sx={{
                      background: 'primary',
                      color: '#fff',
                      fontWeight: 600,
                      borderRadius: '20px',
                      px: 3,
                      py: 1,
                      textTransform: 'none',
                      '&:hover': {
                        background:  'rgb(73, 156, 223)'
                      }
                    }}
                  >
                    {user.planExpiresAt ? 'Upgrade Plan' : 'Choose a Plan'}
                  </Button>
                </Box>
              </Grid>
            )}
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default Profile; 